/**
 * Backdrop-inverted rendering for overlay lines.
 *
 * The wall-skin outline and door/window glyph lines render as the INVERSE of
 * whatever is beneath them: the render pipeline copies the current
 * framebuffer to a texture right before the floorplan overlay pass
 * (`BackdropCapture.capture`, called from PotreeRenderer/EDLRenderer), and a
 * patched LineMaterial fragment shader samples the pixel under each fragment
 * and outputs its complement, tinted in the brand accent — the bright tint
 * over the dark X-Ray, the dark tint over white scan strokes / the light
 * Plan view, per pixel.
 */

import * as THREE from 'three';
import type { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';

/**
 * Brand line tints: saturated variants of the brand purple (same ~270° hue as
 * the `purple` palette in index.css, boosted for viewport contrast) — the
 * bright one where the backdrop reads dark, the dark one where it reads
 * light. THREE.Color converts the hex into the linear working space, so after
 * the shader's output encoding the on-screen color matches these values
 * exactly.
 */
const DEFAULT_LINE_TINT_ON_DARK = '#9757d6';
const LINE_TINT_ON_DARK = new THREE.Color(DEFAULT_LINE_TINT_ON_DARK);
const LINE_TINT_ON_LIGHT = new THREE.Color('#000000');

/**
 * Override the tint used over dark backdrops, or restore the brand default by
 * passing no argument. The dark-theme Plan view wants plain white glyphs (an
 * inverted print-style floor plan) rather than the purple used over the X-Ray
 * heatmap. Mutates the shared uniform color, so every patched material picks
 * it up on the next frame.
 */
export function setLineTintOnDark(color: THREE.ColorRepresentation = DEFAULT_LINE_TINT_ON_DARK): void {
  LINE_TINT_ON_DARK.set(color);
}

export class BackdropCapture {
  private texture: THREE.FramebufferTexture | null = null;
  /** Shared uniform objects — patched materials reference these directly, so
   *  a capture (or resize) propagates without touching the materials. */
  readonly uniforms = {
    tBackdrop: { value: null as THREE.Texture | null },
    uBackdropSize: { value: new THREE.Vector2(1, 1) },
    /** What shows where the canvas is TRANSPARENT: the renderer clears to
     *  alpha 0 (2D maps show through), so unrendered regions display the DOM
     *  background behind the canvas — white in the Plan view, dark in X-Ray.
     *  The shader substitutes this wherever the captured alpha is low. */
    uBackdropFallback: { value: new THREE.Color(0, 0, 0) },
  };

  constructor() {
    // 1×1 black placeholder so the sampler is never unbound before the first
    // capture (lines then render in the bright tint — correct for the dark
    // default).
    const placeholder = new THREE.DataTexture(new Uint8Array([0, 0, 0, 255]), 1, 1);
    placeholder.needsUpdate = true;
    this.uniforms.tBackdrop.value = placeholder;
  }

  /** Copy the CURRENTLY BOUND framebuffer (drawing-buffer sized) into the
   *  backdrop texture. Call right before rendering the overlay scene. */
  capture(renderer: THREE.WebGLRenderer): void {
    const size = renderer.getDrawingBufferSize(new THREE.Vector2());
    if (size.x < 1 || size.y < 1) return;
    if (!this.texture || this.texture.image.width !== size.x || this.texture.image.height !== size.y) {
      this.texture?.dispose();
      this.texture = new THREE.FramebufferTexture(size.x, size.y);
      this.uniforms.tBackdrop.value = this.texture;
      this.uniforms.uBackdropSize.value.set(size.x, size.y);
    }
    renderer.copyFramebufferToTexture(this.texture);
    this.updateFallbackFromDom(renderer);
  }

  /** Resolve the effective page background behind the (transparent) canvas.
   *  The layer that actually shows through is `#olContainer` (a SIBLING
   *  beneath the canvas, carrying the per-view background style) — check it
   *  first, then fall back to the canvas' ancestor chain. */
  private updateFallbackFromDom(renderer: THREE.WebGLRenderer): void {
    const parseBackground = (element: HTMLElement): boolean => {
      const match = getComputedStyle(element).backgroundColor.match(/rgba?\(\s*(\d+)[,\s]+(\d+)[,\s]+(\d+)(?:[,\s/]+([\d.]+))?\s*\)/);
      if (match && (match[4] === undefined || parseFloat(match[4]) > 0)) {
        this.uniforms.uBackdropFallback.value.setRGB(Number(match[1]) / 255, Number(match[2]) / 255, Number(match[3]) / 255);
        return true;
      }
      return false;
    };
    const olContainer = document.getElementById('olContainer');
    if (olContainer && parseBackground(olContainer)) return;
    let element: HTMLElement | null = renderer.domElement instanceof HTMLCanvasElement ? renderer.domElement : null;
    while (element) {
      if (parseBackground(element)) return;
      element = element.parentElement;
    }
  }

  dispose(): void {
    this.texture?.dispose();
    this.texture = null;
    this.uniforms.tBackdrop.value = null;
  }
}

/**
 * Patch a LineMaterial so each fragment renders INVERTED against the
 * backdrop beneath it, tinted in the brand accent: the bright tint where the
 * backdrop reads dark, the dark tint where it reads light.
 *
 * Hard-won shape (see the capture-placement note in PotreeRenderer /
 * EDLRenderer): the noise we fought here was a FEEDBACK loop — a capture
 * taken late in the render pass contained the lines' own previous output,
 * which oscillated regardless of sampling strategy. With the capture taken
 * straight after the point-cloud pass, a single tap on the pixel beneath the
 * fragment is stable. The decision is binary (full contrast), thresholded
 * bright (samples are display-encoded) so mid-gray walls get bright-tint
 * lines and only genuinely light backdrops flip to the dark tint. Tone
 * mapping is disabled so the output stays at full value.
 */
export function applyBackdropInvert(material: LineMaterial, capture: BackdropCapture): void {
  material.toneMapped = false;
  material.onBeforeCompile = (shader) => {
    shader.uniforms.tBackdrop = capture.uniforms.tBackdrop;
    shader.uniforms.uBackdropSize = capture.uniforms.uBackdropSize;
    shader.uniforms.uBackdropFallback = capture.uniforms.uBackdropFallback;
    shader.uniforms.uLineTintOnDark = { value: LINE_TINT_ON_DARK };
    shader.uniforms.uLineTintOnLight = { value: LINE_TINT_ON_LIGHT };
    shader.fragmentShader = shader.fragmentShader
      .replace('void main() {', 'uniform sampler2D tBackdrop;\nuniform vec2 uBackdropSize;\nuniform vec3 uBackdropFallback;\nuniform vec3 uLineTintOnDark;\nuniform vec3 uLineTintOnLight;\nvoid main() {')
      .replace(
        'gl_FragColor = vec4( diffuseColor.rgb, alpha );',
        [
          // The pixel directly beneath the fragment (gl_FragCoord sits on
          // the pixel center, so this reads exactly that texel). Safe to
          // sample this tightly because the capture is taken straight after
          // the point-cloud pass — it can never contain the lines
          // themselves, so there is no feedback to amplify.
          'vec4 backdropSample = texture2D( tBackdrop, gl_FragCoord.xy / uBackdropSize );',
          // Composite like the screen does (premultiplied canvas over the
          // DOM): rendered color PLUS the page background weighted by the
          // remaining transparency. NOTE: not a mix() on alpha — the point
          // materials' blending often leaves destination alpha at 0 even
          // where the cloud rendered, and a mix would then discard the real
          // colors entirely (every line went white).
          'vec3 backdropColor = backdropSample.rgb + uBackdropFallback * ( 1.0 - backdropSample.a );',
          'float backdropLum = dot( backdropColor, vec3( 0.299, 0.587, 0.114 ) );',
          // Soft ramp instead of a hard cut: borderline backdrops fade
          // between the bright and dark tint rather than snapping, so
          // transitions along the line read as antialiasing instead of
          // jagged flips. The band starts above the mid-gray walls (~0.5
          // encoded), which therefore keep fully bright-tint lines.
          'gl_FragColor = vec4( mix( uLineTintOnDark, uLineTintOnLight, smoothstep( 0.55, 0.80, backdropLum ) ), alpha );',
        ].join('\n'),
      );
  };
  material.needsUpdate = true;
}
